import { CloseOutlined } from "@ant-design/icons"
import { Button, Flex, Tooltip, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

const { Text } = Typography

// Same sticky-bottom-bar convention as `LeadsBulkActionBar`/
// `MilestonesBulkActionBar` — dark teal toolbar (`#17383E`), teal action
// buttons (`#2A7D8C`/`#236b78` hover), "N selected" count, a trailing Clear
// action pinned right. A single, no-modal action button ("Show on Map"),
// closer in shape to Leads' own single-click Export button than to any of
// its modal-opening actions — there is no input to collect, no async
// result/failure state to report inline (a failed fetch surfaces as a plain
// toast, the same lightweight convention `handleLocateMe` already uses
// elsewhere on this page).
interface GlobalMapListBulkActionBarProps {
  selectedCount: number
  showOnMapDisabled?: boolean
  showOnMapLoading?: boolean
  onShowOnMap: () => void
  onClear: () => void
}

const buttonClass =
  "!h-9 !border-[#2A7D8C] !bg-[#2A7D8C] !text-white hover:!border-[#236b78] hover:!bg-[#236b78]"

const GlobalMapListBulkActionBar = ({
  selectedCount,
  showOnMapDisabled,
  showOnMapLoading,
  onShowOnMap,
  onClear
}: GlobalMapListBulkActionBarProps) => {
  const { t } = useTranslation()

  if (!selectedCount) return null

  return (
    <div
      className="sticky bottom-0 z-30"
      data-testid="global-map-list-bulk-action-bar"
    >
      <Flex
        align="center"
        gap="small"
        className="overflow-x-auto bg-[#17383E] px-4 py-3 text-white"
        role="toolbar"
        aria-label={t("globalMap.list.bulkActions.toolbarLabel")}
        wrap={false}
      >
        <Text strong className="mr-2 whitespace-nowrap !text-white">
          {t("globalMap.list.bulkActions.selected", { count: selectedCount })}
        </Text>

        <Tooltip
          title={
            showOnMapDisabled
              ? t("globalMap.list.bulkActions.showOnMapDisabled")
              : undefined
          }
        >
          <Button
            className={buttonClass}
            disabled={showOnMapDisabled}
            loading={showOnMapLoading}
            onClick={onShowOnMap}
          >
            {t("globalMap.list.bulkActions.showOnMap")}
          </Button>
        </Tooltip>

        <Tooltip title={t("globalMap.list.bulkActions.clearSelection")}>
          <Button
            // The fixed support launcher expands to 56px from the right edge
            // (same app-wide widget `LeadsBulkActionBar` reserves space for)
            // — its trailing margin keeps this dismiss action clickable even
            // while the launcher is open.
            className="ml-auto mr-16 shrink-0 !text-white"
            type="text"
            icon={<CloseOutlined />}
            aria-label={t("globalMap.list.bulkActions.clearSelection")}
            onClick={onClear}
          />
        </Tooltip>
      </Flex>
    </div>
  )
}

export default GlobalMapListBulkActionBar
